import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  SohoAlertDirective,
  SohoBusyIndicatorDirective,
  SohoMessageService,
  SohoSpinboxComponent
} from 'ids-enterprise-ng';
import { Subscription } from 'rxjs';

import { LMPrintCsvInfo, LMPrintDialog } from '@modal/print-dialog/lm-print-dialog.service';
import { LMPrintRequestService } from '@modal/print-dialog/lm-print-request.service';

import { AppConstants } from '@utils/app.constants';
import { LMLookupBasicComponent } from '@field/lookup';
import { LMFieldValueChangedEvent, LMFormRequestService } from '@app/components';
import { LMUrlUtilService } from '@app/services';
import { LMListDropdownComponent } from '@components/lmlistdropdown';
import { LMGlobalPersonalizationService } from '@app/states';

@Component({
  selector: 'lm-print-csv-dialog',
  templateUrl: './lm-print-csv-dialog.component.html',
  styleUrls: [ './lm-print-csv-dialog.component.css' ]
})
export class LMPrintCsvDialogComponent implements LMPrintDialog, AfterViewInit, OnDestroy, OnInit {
  constructor(
    private sohoMessageService: SohoMessageService,
    private formBuilder: FormBuilder,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmPrintRequestService: LMPrintRequestService,
    private lmFromRequestService: LMFormRequestService) {}

  @ViewChild('modalPlaceholder', { read: ViewContainerRef, static: true }) modalPlaceholder: ViewContainerRef;
  @ViewChild('maxLevels') maxLevels: SohoSpinboxComponent;
  @ViewChild('maxRows') maxRows: SohoSpinboxComponent;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChildren(SohoAlertDirective) alerts: QueryList<SohoAlertDirective>;
  @ViewChild(LMLookupBasicComponent) lmLookupBasicCsv: LMLookupBasicComponent;
  @ViewChild('dynamicActorsComponent') dynamicActorsComponent: LMListDropdownComponent;

  @Input() printInfo: LMPrintCsvInfo;

  private subscriptions: Subscription[] = [];

  dynamicActorOptions: LMListDropdownOptions = {
    businessClass: 'Actor',
    maxValues: 20,
    searchFields: [ 'Actor', 'PersonName.GivenName',  'PersonName.FamilyName', 'PersonName.PreferredGivenName' ],
    selectList: 'AdHocDistributionGroupActorList' };
  showDynamicActors = false;
  atLeastOneDynamicActor = false;
  dynamicActorsLabel = Soho.Locale.translate('PDFAdHocActors');

  reportDistGroupLabel = '';
  sourceFieldName = '';

  readonly formatOptions: LMPrintOption[] = [
    { label: 'CSV', value: 1 },
    { label: 'Excel', value: 2 },
  ];

  readonly columnHeaderOptions: LMPrintOption[] = [
    { label: 'FieldID', value: 0 },
    { label: 'FieldLabel', value: 1 },
  ];

  readonly exportOptions: LMPrintOption[] = [
    { label: Soho.Locale.translate('PDFAllLevels'), value: AppConstants.PDF_EXPANDED_ACTION },
    { label: Soho.Locale.translate('PDFSpecifiedMaxLevels'), value: AppConstants.PDF_SPECIFIED_ACTION },
  ];

  printForm: FormGroup;
  allRecordsAlert: SohoAlertDirective;
  dynamicActorsAlert: SohoAlertDirective;
  useCompactFormLayout = false;
  useTabs = true;
  showMaxRows = false;
  showMaxLevels = false;

  ngOnInit() {
    this.useCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;
    // Setup form with default values
    this.printForm = this.formBuilder.group({
      csvFormat: [ 1, Validators.required ],
      columnHeaderType: [ 0, Validators.required ],
      name: [ '', Validators.required ],
      maxRows: [ 5000, Validators.required ],
      recordLimitOption: [ 'default', Validators.required ],
      allRecords: [ false, Validators.required ],
      exportOption: [ { value: 0, disabled: true }, Validators.required ],
      maxLevels: [ { value: 3, disabled: true }, Validators.required ],
    });
    const controls = this.printForm.controls;
    controls.name.setValue(this.printInfo.name);

    if (!this.printInfo.includeRecordLimit && !this.printInfo.isTreeList && !this.isUseAllActorGroups) {
      this.useTabs = false;
    }

    if (!this.printInfo.includeRecordLimit) {
      controls.allRecords.disable();
      controls.recordLimitOption.disable();
      controls.maxRows.disable();
    }

    if (this.printInfo.isTreeList) {
      controls.exportOption.enable();
      controls.exportOption.setValue(AppConstants.PDF_EXPANDED_ACTION);
    }
    this.reportDistGroupLabel = Soho.Locale.translate('PDFRepDistGroup');
    this.sourceFieldName = 'ReportDistributionGroup';
  }

  ngAfterViewInit() {
    const controls = this.printForm.controls;
    let subscription: Subscription;
     // exportOption value determines if maxLevels field enable/disable
    subscription = controls.exportOption.valueChanges.subscribe((value) => {
      const maxLevelsControl = controls.maxLevels;
      const disable = Number(value) !== AppConstants.PDF_SPECIFIED_ACTION;
      this.showMaxLevels = !disable;
      if (disable) {
        maxLevelsControl.disable();
      } else {
        maxLevelsControl.enable();
      }
    });
    this.subscriptions.push(subscription);

    subscription = controls.recordLimitOption.valueChanges.subscribe((value) => {
      const maxRowsControl = this.printForm.get('maxRows');
      const allRecordsControl = this.printForm.get('allRecords');
      allRecordsControl.setValue(value === 'all');
      this.showMaxRows = value === 'custom';
      this.allRecordsAlert?.removeAllMessages();

      switch (value) {
        case 'all':
          maxRowsControl.disable();
          const alertMessage =  Soho.Locale.translate('PDFIncludingAllRecords');
          this.allRecordsAlert?.addInlineMessage(alertMessage, 'info', true);
          break;
        case 'default':
          maxRowsControl.enable();
          maxRowsControl.setValue(5000);
          break;
        case 'large':
          maxRowsControl.enable();
          maxRowsControl.setValue(250000);
          break;
        case 'small':
          maxRowsControl.enable();
          maxRowsControl.setValue(100);
          break;
        case 'custom':
          maxRowsControl.enable();
          maxRowsControl.setValue(5000);
      }
    });
    this.subscriptions.push(subscription);

    if (this.lmLookupBasicCsv) {
      // Set the select list url for ReportDistributionGroup
      this.lmLookupBasicCsv.viewName = 'CSVOptions'; // Dummy data for handcoded forms. (There is no formSpec or fromDataView)
      this.lmLookupBasicCsv.selectListUrl = this.lmPrintRequestService.getReportDistributionSelectUrl();
    }

    this.allRecordsAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'recordLimitAlert');
    this.dynamicActorsAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'dynamicActors');

    if (this.printInfo.includeRecordLimit) {
      const exportDefaultRecordLimit = this.lmGlobalPersonalizationService.getGlobalPersonalization().exportDefaultRecordLimit;
      if (exportDefaultRecordLimit === 'no-limit') {
        controls.recordLimitOption.setValue('all');
        controls.maxRows.disable();
        const alertMessage =  Soho.Locale.translate('PDFIncludingAllRecords');
        this.allRecordsAlert?.addInlineMessage(alertMessage, 'info', true);
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get resource(): Resource {
    return this.printInfo.resource;
  }

  get isUseAllActorGroups(): boolean {
    return this.lmPrintRequestService.getConfigFeatureUseAllActorGroups();
  }

  public checkIfFormIsValid(): boolean {
    let isValid = true;
    if (this.atLeastOneDynamicActor) {
      isValid = this.dynamicActorsComponent.values.length > 0;
      if (!isValid) {
        // this.dynamicActorsComponent.dropdown.setFocus();
        const options: SohoMessageOptions = {
          title: Soho.Locale.translate('ValidationError'),
          message: Soho.Locale.translate('PDFAtLeastOneDynamicActor'),
          showCloseBtn: true };

        this.sohoMessageService
          .alert(options)
          .open();
      }
    }
    return isValid;
  }

  get requestData(): LMPrintRequestData {
    // Default scheduleView derived from landmark.popupmenu.js
    let value = this.printForm.controls.name.value;
    value = `${value} - ${Soho.Locale.translate('ExportToCSV')}`;

    const scheduleView = { fields: { Name: { value } } };
    const requestData = <LMPrintRequestData>{
      requestType: 'scheduleCsv',
      scheduleView
    };

    if (this.printInfo.dataView) {
      requestData.dataView = this.printInfo.dataView;
    }

    if (this.printInfo.selectRequestData) {
      requestData.selectRequestData = this.printInfo.selectRequestData;
    }

    if (this.showDynamicActors && this.dynamicActorsComponent) {
      requestData.dynamicActors = this.dynamicActorsComponent.values;
    }
    return requestData;
  }

  /**
   * Returns configured form values
   * @return
   */
  get requestParams(): LMPrintRequestParams {
    const commonParams = this.lmPrintRequestService.getCommonRequestParams(this.printInfo);

    const params =  <LMPrintRequestParams>{
      output: 'csv',
      ...this.printForm.value,
      ...commonParams
    };

    if (this.printInfo.chartName) {
      params.chartName = this.printInfo.chartName;
    }

    // Lookup component does not implement ControlAccessorValue. Landmark forms does not use formgroup / formcontrol to manage model data.
    if (this.lmLookupBasicCsv && this.lmLookupBasicCsv.lookupValue) {
      params.reportDistGroup = this.lmLookupBasicCsv.lookupValue;
    }

    if (this.printInfo.isTreeList && !params.hasOwnProperty('maxLevels')) {
      params.maxLevels = -1;
    }

    return params;
  }

  /**
   * Returns Toast Success Message
   * @param          if csv is scheduled
   * @return
   */
  getSuccessMessage(scheduled: boolean): SohoToastOptions {
    const title = Soho.Locale.translate(scheduled ? 'CSVIsScheduled' : 'GeneratingCSV');
    const message = Soho.Locale.translate('PleaseVisitMyPrintFiles');

    return { title, message };
  }

  /**
   * Returns Toast Error Message
   * @param          if csv is scheduled
   * @return
   */
  getErrorMessage(scheduled: boolean): SohoToastOptions {
    const title = Soho.Locale.translate(scheduled ? 'CSVScheduleError' : 'CSVGenerationError');
    const message = Soho.Locale.translate(scheduled ? 'CSVWasNotScheduled' : 'CSVWasNotGenerated');

    return { title,  message };
  }

  reportDistributionGroupChanged(event: LMFieldValueChangedEvent) {
    const key = this.lmLookupBasicCsv?.lookupValue;
    this.dynamicActorsAlert.removeAllMessages();

    if (!key) {
      this.dynamicActorsLabel = Soho.Locale.translate('PDFAdHocActors');
      this.showDynamicActors = false;
      this.atLeastOneDynamicActor = false;
      return;
    }

    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    const resourceId = encodeURIComponent(`ReportDistributionGroup(${key})`);
    const url = `${baseUrl}/form/${resourceId}`;
    const fieldList = [ 'HasDynamicActorReportDistribution', 'DynamicActorsOnlyProfiles' ];

    this.lmFromRequestService.getFieldValues(url, fieldList).subscribe(response => {
      let showActors = false;
      let requiresAtLeastOneActor = false;

      if (response?.status === 'Completed') {
        const fields = response.fields;
        if (fields.hasOwnProperty('HasDynamicActorReportDistribution') && fields.hasOwnProperty('DynamicActorsOnlyProfiles')) {
          const hasDynamicActorsFld: LMField = fields.HasDynamicActorReportDistribution;
          const onlyDynamicActorsFld: LMField = fields.DynamicActorsOnlyProfiles;
          showActors = hasDynamicActorsFld.value as boolean;
          requiresAtLeastOneActor = onlyDynamicActorsFld.value as boolean;
        }
      }

      this.dynamicActorsLabel = requiresAtLeastOneActor ? Soho.Locale.translate('PDFRequiredAdHocActors') : Soho.Locale.translate('PDFAdHocActors');
      this.showDynamicActors = showActors;
      this.atLeastOneDynamicActor = requiresAtLeastOneActor;

      if (showActors) {
        const alertMessage = requiresAtLeastOneActor ? Soho.Locale.translate('PDFNeedAtLeastOneActor') : Soho.Locale.translate('PDFAddAdditionalActors');
        this.dynamicActorsAlert.addInlineMessage(alertMessage, 'info', true);
      }
    });
  }
}
